/**
 * Synapsy Widget - Identidade Cortexy (host first-party)
 *
 * Resolve `user_id`/`session_id` no contexto da PÁGINA HOST, onde o widget roda.
 * Ao contrário do iframe (origem de terceiro, com storage bloqueado/particionado
 * em webview mobile), o `localStorage` da host é first-party e PERSISTE entre
 * reloads e navegações. O widget injeta esses ids na URL do iframe; o `tracker.ts`
 * (dentro do iframe) os lê com precedência via `fromUrl()`.
 *
 * É o que faz a janela de sessão de 1h ser respeitada também no mobile: conversas
 * novas dentro da janela reusam o mesmo `session_id`; um novo só após o timeout.
 *
 * Espelha intencionalmente a lógica de `frontend/src/utils/tracker.ts` e do SDK
 * Cortexy. A duplicação é inerente: host e iframe são contextos de browser
 * distintos que não compartilham storage nem runtime.
 */

const USER_KEY = "cortexy_user_id";
const SESSION_KEY = "cortexy_session";
const SESSION_TIMEOUT_MS = 3_600_000; // 1h — idêntico ao SDK Cortexy e ao tracker.ts

let memoryUserId = null;
let memorySession = null; // formato "uuid:ts"

// Acessar window.localStorage/sessionStorage (e get/setItem) pode lançar em
// webview com storage de terceiro bloqueado → toda operação é isolada aqui.
function read(kind, key) {
  try {
    const store = kind === "local" ? window.localStorage : window.sessionStorage;
    return store.getItem(key) || null;
  } catch {
    return null;
  }
}

function write(kind, key, value) {
  try {
    const store = kind === "local" ? window.localStorage : window.sessionStorage;
    store.setItem(key, value);
  } catch {
    /* storage cheio/bloqueado: ignora */
  }
}

function generateUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function parseSession(raw) {
  if (!raw) return null;
  const parts = raw.split(":");
  if (parts.length !== 2) return null;
  const ts = parseInt(parts[1], 10);
  if (Number.isNaN(ts) || !parts[0]) return null;
  return { uuid: parts[0], ts };
}

/**
 * Resolve o user_id (device persistente).
 * @param {string} [preferredId] - id conhecido fornecido pelo embedder
 *   (window.synapsyConfig.userId); tem prioridade sobre o id gerado.
 * @returns {string} user_id (nunca vazio, nunca "anonymous")
 */
export function resolveUserId(preferredId) {
  // Precedência: id do embedder → storage → memória → gera. Sempre persistido.
  let id = preferredId || read("local", USER_KEY) || read("session", USER_KEY) || memoryUserId;
  if (!id) id = generateUuid();
  write("local", USER_KEY, id);
  write("session", USER_KEY, id);
  memoryUserId = id;
  return id;
}

/**
 * Resolve a sessão (visita) com janela rolante de 1h.
 * Reusa o uuid se a sessão guardada está dentro da janela; senão rotaciona.
 * @returns {string} "uuid:timestamp_ms" — valor injetado no param cortexy_session
 */
export function resolveSession() {
  const parsed = parseSession(
    read("local", SESSION_KEY) || read("session", SESSION_KEY) || memorySession,
  );

  const now = Date.now();
  const uuid = parsed && now - parsed.ts < SESSION_TIMEOUT_MS ? parsed.uuid : generateUuid();

  const value = `${uuid}:${now}`; // refresh do timestamp (janela rolante)
  write("local", SESSION_KEY, value);
  write("session", SESSION_KEY, value);
  memorySession = value;
  return value;
}
